import React, { useState, useEffect } from "react";
import galleryData from "../gallery.json"; // Ensure the path is correct
import SEOHead from "../components/SEOHead";

const Gallery = () => {
	// Get section names from the JSON file
	const sectionKeys = Object.keys(galleryData);
	// Set the default section to the first key found in the JSON file
	const [currentSection, setCurrentSection] = useState(sectionKeys[0]);
	const [currentImageIndex, setCurrentImageIndex] = useState(0);
	const [showModal, setShowModal] = useState(false);

	// Auto-slide images every 4 seconds when modal is closed
	useEffect(() => {
		if (!showModal) {
			const totalImages = galleryData[currentSection].files.length;
			const interval = setInterval(() => {
				setCurrentImageIndex(
					(prevIndex) => (prevIndex + 1) % totalImages
				);
			}, 4000);
			return () => clearInterval(interval);
		}
		// If modal is open, no interval is needed
	}, [currentSection, showModal]);

	const handleSectionChange = (section) => {
		setCurrentSection(section);
		setCurrentImageIndex(0);
	};

	const handleImageClick = (index) => {
		setCurrentImageIndex(index);
		setShowModal(true);
	};

	const closeModal = () => setShowModal(false);

	const navigateModal = (direction) => {
		const totalImages = galleryData[currentSection].files.length;
		setCurrentImageIndex((prevIndex) =>
			direction === "next"
				? (prevIndex + 1) % totalImages
				: (prevIndex - 1 + totalImages) % totalImages
		);
	};

	return (
		<>
			<SEOHead
				title="School Photos & Events - See What We Do | Sacred Heart School"
				description="Check out our school gallery! See photos from our sports day, cultural programs, annual functions, and daily activities. Get a feel for life at Sacred Heart School."
				keywords="school photos, school events pictures, annual function photos, sports day, cultural program, what happens at school, school life photos"
				canonicalUrl="https://sacredheartchumoukedima.com/gallery"
				ogTitle="Glimpse Into Our School Life - Photo Gallery"
				ogDescription="From sports meets to cultural celebrations - see our students in action! Browse our photo collection."
			/>
			<section className="bg-gray-100 py-12">
			<div className="mx-auto px-4">
				<h2 className="text-4xl font-bold text-center text-theme mb-8">
					Gallery
				</h2>

				{/* Section Tabs */}
				<div className="flex flex-wrap justify-center gap-3 mb-8">
					{sectionKeys.map((section) => (
						<button
							key={section}
							onClick={() => handleSectionChange(section)}
							className={`px-4 py-2 text-sm font-semibold rounded-lg transition ${
								currentSection === section
									? "bg-theme text-white"
									: "bg-white text-theme border border-theme hover:bg-theme hover:text-white"
							}`}
						>
							{section}
						</button>
					))}
				</div>

				{/* Image Slider */}
				<div className="relative w-full md:w-1/3 mx-auto overflow-hidden rounded-lg shadow-xl mb-8">
					<img
						src={`/assets/images/gallery${galleryData[currentSection].files[currentImageIndex]}`}
						alt={`${currentSection} ${currentImageIndex + 1}`}
						className="w-full aspect-square object-cover rounded-lg"
					/>
				</div>

				{/* Fancy Image Grid */}
				<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
					{galleryData[currentSection].files.map((image, index) => (
						<div
							key={index}
							onClick={() => handleImageClick(index)}
							className="relative group cursor-pointer overflow-hidden rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
						>
							<img
								src={`/assets/images/gallery${image}`}
								alt={`Thumbnail ${index + 1}`}
								className="w-full h-full object-cover"
							/>
							{/* Overlay */}
							<div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-0 group-hover:opacity-80 transition-opacity duration-300"></div>
							{/* Text Overlay */}
							<div className="absolute bottom-4 left-4 text-white text-lg font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
								{/* {currentSection} {index + 1} */}
							</div>
						</div>
					))}
				</div>
			</div>

			{/* Modal */}
			{showModal && (
				<div
					id="modal-overlay"
					className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
					onClick={(e) => {
						if (e.target.id === "modal-overlay") closeModal();
					}}
				>
					<div className="relative w-11/12 md:w-2/3 lg:w-1/2">
						<button
							onClick={closeModal}
							className="absolute top-4 right-4 text-xl px-2 text-gray-200 font-bold bg-gray-800 rounded-full shadow-md hover:bg-gray-600"
						>
							&times;
						</button>
						<img
							src={`/assets/images/gallery${galleryData[currentSection].files[currentImageIndex]}`}
							alt={`Modal ${currentImageIndex + 1}`}
							className="w-full h-[70vh] object-contain bg-white rounded-lg"
						/>
						<div className="flex justify-between mt-4">
							<button
								onClick={() => navigateModal("prev")}
								className="text-white text-xl bg-theme px-4 py-2 rounded"
							>
								Prev
							</button>
							<button
								onClick={() => navigateModal("next")}
								className="text-white text-xl bg-theme px-4 py-2 rounded"
							>
								Next
							</button>
						</div>
					</div>
				</div>
			)}
			</section>
		</>
	);
};

export default Gallery;
